import React, { useState, useEffect, useRef, useActionState } from "react";
import { Editor } from "primereact/editor";
import {
  MDXEditor,
  MDXEditorMethods,
  headingsPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  toolbarPlugin,
  tablePlugin,
  linkDialogPlugin,
  directivesPlugin,
  diffSourcePlugin,
  frontmatterPlugin,
  imagePlugin,
  listsPlugin,
  thematicBreakPlugin,
  BlockTypeSelect,
  InsertTable,
  InsertFrontmatter,
  InsertThematicBreak,
  CreateLink,
  ListsToggle,
} from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";
import { highlightPlugin } from "mdxe-plugin-highlight";
import { TabView, TabPanel } from "primereact/tabview";
import { Button } from "primereact/button";
import { AIButton } from "./AIButton";
import "./NotesModal.css"; // Import the CSS file for styling
// import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import Dropdown from "react-dropdown";
import "react-dropdown/style.css";
import CustomAlert from "./CustomAlert";
import { generateAiDocument } from "../Helpers/ApiChatGpt";
import { generateFocusAiDocument } from "../Helpers/ApiFocus";
import LinkPreview from "./LinkPreview";
import {
  getCompanyDataByCVR,
  getCVRdata,
  getCompanyDataByName,
} from "../Helpers/cvr";
import { useStore } from "../App/store";
import {
  generatePubMedDocument,
  getPubMedArticles,
} from "../Helpers/ApiPubMed";
import PdfViewer from "./PdfViewer";
import { CgWebsite } from "react-icons/cg";
import { Dialog } from "primereact/dialog";
import { NodeData, KanbanCardData, Event, Address, Task } from "../types/types";
import ReactFlow, { Node } from "reactflow";
import KanbanCard from "./KanbanCard";
import { updateNodesWithEditCard } from "./kanbanHelpers";
import EventCard from "./EventCard";
import AddressCard from "./AddressCard";

interface NotesModalProps {
  node: Node<NodeData>;
  nodes: Node<NodeData>[];
  isOpen: boolean;
  handleSaveNotes: (
    notes: string,
    links: {
      title: string;
      url: string;
    }[]
  ) => void;
  handleCancelEditNotes: () => void;
  aiMode: boolean;
  setAIMode?: (mode: boolean) => void;
  handleAddUpdateEvent: (
    id: string,
    nodeTitle: string,
    title: string,
    description: string,
    date: Date | undefined
  ) => void;
  handleDeleteEvent: (id: string) => void;
  handleAddUpdateAddress?: (
    id: string,
    nodeTitle: string,
    name: string,
    street: string,
    zip_code: string,
    city: string,
    Phone: string,
    email: string,
    vat: string,
    employees: string,
    notes: string,
    latitude?: number,
    longitude?: number
  ) => void;
  handleDeleteAddress?: (id: string) => void;
  handleAddUpdateTask?: (
    id: string | number,
    title: string,
    nodeTitle: string,
    status: string,
    summary?: string,
    priority?: string,
    tags?: string,
    assignee?: string,
    estimate?: number,
    rankId?: number,
    color?: string,
    className?: string,
    startDate?: Date,
    endDate?: Date
  ) => void;
  handleDeleteTask?: (id: string | number) => void;
}

const NotesModal: React.FC<NotesModalProps> = ({
  node,
  nodes,
  isOpen,
  handleSaveNotes,
  handleCancelEditNotes,
  aiMode,
  setAIMode,
  handleAddUpdateEvent,
  handleDeleteEvent,
  handleAddUpdateAddress,
  handleDeleteAddress,
  handleAddUpdateTask,
  handleDeleteTask,
}) => {
  const [currentNotes, setCurrentNotes] = useState(node?.data.notes);
  const [currentLinks, setCurrentLinks] = useState(node?.data.links || []);
  const [currentLink, setCurrentLink] = useState({ title: "", url: "" });
  const quillRef = useRef<Editor | null>(null);
  const ref = React.useRef<MDXEditorMethods>(null);
  const [source, setSource] = useState("select source");
  const [topic, setTopic] = useState("");
  const [alertTitle, setAlertTitle] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showAddLinkModal, setShowAddLinkModal] = useState(false);
  const aiResponse = useStore.getState().aiResponse;
  const [editKanbanCard, setEditKanbanCard] = useState<KanbanCardData | null>(
    null
  );
  const [kanbanCardItems, setKanbanCardItems] = useState<KanbanCardData[]>([]);
  const [showEventCard, setShowEventCard] = useState(false);
  const [showLocationCard, setShowLocationCard] = useState(false);
  const [showKanbanCard, setShowKanbanCard] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCurrentNotes(node?.data.notes);
      setCurrentLinks(node?.data.links || []);

      // Position cursor at the end of the Quill editor content
      setTimeout(() => {
        if (quillRef.current) {
          const quill = (quillRef.current as any).getQuill();
          if (quill) {
            const length = quill.getLength();
            quill.setSelection(length, 0);
            quill.focus();
          }
        }
      }, 100);
    }
  }, [isOpen, node?.data.notes, node?.data.links]);

  useEffect(() => {
    if (typeof aiResponse === "string" && aiResponse.trim() !== "") {
      setCurrentNotes(aiResponse);
      useStore.getState().setAIResponse("");
    }
  }, [aiResponse]);

  // Ensure MDXEditor updates when currentNotes changes
  useEffect(() => {
    if (ref.current && typeof currentNotes === "string") {
      ref.current.setMarkdown(currentNotes);
    }
  }, [currentNotes]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleCancelEditNotes();
      }

      // Ctrl/Cmd + S to save notes
      if (event.key === "s" && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        handleSaveNotesAndLinks();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    } else {
      window.removeEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleCancelEditNotes, currentNotes, currentLinks]);

  const handleSaveNotesAndLinks = async () => {
    let notesToSave = currentNotes || "";
    handleSaveNotes(notesToSave, currentLinks);
  };

  const handleCancel = () => {
    handleCancelEditNotes();
    setCurrentNotes("");
  };

  // function handleSaveKanbanCard(
  //   event?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  // ): void {
  //   if (!editKanbanCard) return;

  //   // Update the kanbanCardItems state with the edited card
  //   setKanbanCardItems((prevItems) =>
  //     prevItems.map((item) =>
  //       item.id === editKanbanCard.id ? { ...item, ...editKanbanCard } : item
  //     )
  //   );

  //   // Optionally, update the parent data if needed
  //   if (handleAddUpdateTask) {
  //     // Find the corresponding node and update its data
  //     const updatedNodes = updateNodesWithEditCard(nodes, editKanbanCard);
  //     handleAddUpdateTask(updatedNodes);
  //   }

  //   setEditKanbanCard(null);
  // }

  const switchToNotesTab = (key: string) => {
    const tabs = document.querySelectorAll(".react-tabs__tab");
    const notesTab = Array.from(tabs).find((tab) =>
      tab.textContent?.toLowerCase().includes(key)
    );
    if (notesTab) {
      (notesTab as HTMLElement).click();
    }
  };

  let result: any;
  let cvrResult = "";
  let htmlResult = "";
  const getInsightsFromSource = async () => {
    if (topic?.trim() === "") {
      showAlert("Notice", "Please enter a topic to get insights.");
      return;
    }
    switchToNotesTab("notes");

    switch (source.toLowerCase()) {
      case "cvr":
        setLoading(true);
        //cvrResult = (await getCVRinfo(topic)) ?? null;
        if (!isNaN(Number(topic))) {
          cvrResult = await getCompanyDataByCVR(topic);
        } else {
          cvrResult = await getCompanyDataByName(topic);
        }
        const cvrData = cvrResult || "No data received from CVR.";
        setCurrentNotes(cvrData);
        setLoading(false);
        break;
      case "tinglysning":
      case "statstidende":
      case "retsinformation":
      case "biq":
        showAlert(
          "Notice",
          `The ${source.toUpperCase()} integration is work in progress.`
        );
        break;
      case "chatgpt":
        setLoading(true);
        result = await generateAiDocument(topic);

        let bodyContent = result.match(/<body[^>]*>((.|[\n\r])*)<\/body>/im);
        setCurrentNotes(bodyContent ? bodyContent[1] : result);
        setLoading(false);
        break;
      case "focusai":
        setLoading(true);
        result = await generateFocusAiDocument(topic);
        //bodyContent = result.match(/<body[^>]*>((.|[\n\r])*)<\/body>/im);
        setCurrentNotes(result ? result.toString() : "");
        setLoading(false);
        break;
      case "pubmed":
        setLoading(true);
        // Step 1: Get Article Ids
        result = await getPubMedArticles(topic);
        //console.log("PubMed search result", result);
        const textResult = result.join(",");
        //setCurrentNotes(textResult);

        // Step 2: Get Article Details
        const response = await generatePubMedDocument(result);
        //console.log("PubMed document result", response.data);
        htmlResult = typeof response === "string" ? response : response.data;
        // console.info("HTML Result", htmlResult);

        // Parse the XML string
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(htmlResult, "text/xml");

        const htmlContainer = document.createElement("div");

        // Extract data
        const articles = xmlDoc.querySelectorAll("PubmedArticle");
        articles.forEach((article) => {
          const pmid = article.querySelector("PMID")?.textContent || "";
          const journalTitle =
            article.querySelector("Journal > Title")?.textContent || "";
          const articleTitle =
            article.querySelector("ArticleTitle")?.textContent || "";
          const abstractSections =
            article.querySelectorAll("AbstractText") || [];

          // Create HTML elements for each article
          const articleContainer = document.createElement("div");
          articleContainer.innerHTML = `
            <h1>${articleTitle}</h1>
            <p><strong>Journal:</strong> ${journalTitle}</p>
            <p><strong>PMID:</strong> ${pmid}</p>
            <h2>Abstract</h2>
          `;

          // Append abstract sections
          abstractSections.forEach((section) => {
            const label = section.getAttribute("Label") || "Section";
            const text = section.textContent;
            const sectionElem = document.createElement("p");
            sectionElem.innerHTML = `<strong>${label}:</strong> ${text}`;
            sectionElem.innerHTML += "<br/><br/>";
            articleContainer.appendChild(sectionElem);
          });

          articleContainer.innerHTML += "<br/><br/><br/>";

          htmlContainer.appendChild(articleContainer);
        });

        setCurrentNotes(htmlContainer.innerHTML);

        setLoading(false);
        break;
      default:
        showAlert("Notice", "Please select a source of knowledge.");
        break;
    }
  };

  const showAlert = (title: string, message: string) => {
    setAlertTitle(title);
    setAlertMessage(message);
  };

  if (!isOpen) {
    return null;
  }

  const handleAddLink = () => {
    setShowAddLinkModal(true);
  };

  const handleSaveLink = () => {
    if (!currentLink.url.match(/^https?:\/\/.+/)) {
      showAlert(
        "Invalid URL",
        "Please enter a valid URL starting with http:// or https://"
      );
      return;
    }
    setCurrentLinks([...currentLinks, currentLink]);
    setCurrentLink({ title: "", url: "" });
    setShowAddLinkModal(false);
  };

  const handleCloseLinkModal = () => {
    setCurrentLink({ title: "", url: "" });
    setShowAddLinkModal(false);
  };

  const handleDeleteLink = (index: number) => {
    const updatedLinks = currentLinks.filter((_, i) => i !== index);
    setCurrentLinks(updatedLinks);
  };

  const handleSetAIButton = (value: boolean) => {
    setAIMode(value);
  };

  const addUpdateEvent = (
    id: string,
    nodeTitle: string,
    title: string,
    description: string,
    date: Date
  ) => {
    if (!title || !date) {
      alert("Title and date are required to save the event.");
      return;
    }
    handleAddUpdateEvent(
      node.id,
      nodeTitle,
      title,
      description || "",
      date || new Date()
    );
    setShowEventCard(false);
  };

  const deleteEvent = (id: string) => {
    handleDeleteEvent(id);
    setShowEventCard(false);
  };

  const addUpdateAddress = async (
    id: string,
    nodeTitle: string,
    name: string,
    street: string,
    zip_code: string,
    city: string,
    Phone: string,
    email: string,
    vat: string,
    employees: string,
    notes: string,
    latitude?: number,
    longitude?: number
  ) => {
    if (!name || !street || !zip_code || !city) {
      alert(
        "Name, street, zip code, and city fields are required to save the address."
      );
      return;
    }
    handleAddUpdateAddress(
      node.id,
      nodeTitle,
      name,
      street,
      zip_code,
      city,
      Phone,
      email,
      vat,
      employees,
      notes,
      latitude,
      longitude
    );
    setShowLocationCard(false);
  };

  const deleteAddress = (id: string) => {
    handleDeleteAddress(id);
    setShowLocationCard(false);
  };

  const addUpdateTask = (
    id: string | number,
    title: string,
    nodeTitle: string,
    status: string,
    summary?: string,
    priority?: string,
    tags?: string,
    assignee?: string,
    estimate?: number,
    rankId?: number,
    color?: string,
    className?: string,
    startDate?: Date,
    endDate?: Date
  ): Task => {
    if (!title || !startDate || !endDate) {
      alert("Title and dates are required to save the event.");
      return;
    }
    handleAddUpdateTask(
      node.id,
      nodeTitle,
      title,
      status,
      summary,
      priority,
      tags,
      assignee,
      estimate,
      rankId,
      color,
      className,
      startDate,
      endDate
    );
    setShowKanbanCard(false);
  };

  const deleteTask = (id: string) => {
    handleDeleteTask(id);
    setShowKanbanCard(false);
  };

  return (
    // <PrimeReactProvider value={{ zIndex: { modal: 1, overlay: 2 } }}>
    <Dialog
      visible={isOpen}
      onHide={handleCancel}
      header={node?.data.label || "Notes"}
      className="notes-modal-dialog"
      style={{
        width: "50vw",
        height: "66vh",
        minWidth: "300px",
        minHeight: "400px",
      }}
      closable={true}
      modal
      blockScroll
      resizable={true}
      onResizeEnd={(e) => {
        // Dialog is resizable
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* <div className="notes-modal-insights">
          <div className="source-dropdown-item">
            <Dropdown
              data-tooltip="Source of knowledge"
              key={"source-dropdown"}
              className="source-dropdown"
              options={useStore.getState().sourceOptions}
              value={source}
              onChange={(source) => setSource(source.value)}
              placeholder="select source"
            />
          </div>

          <div>
            <input
              className="topic-input"
              type="text"
              placeholder={`Ask ${source ? source : "select source"}...`}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  getInsightsFromSource();
                }
              }}
            />
          </div>

          <div className="menu-item narrow">
            <Button
              icon={
                <i
                  className="pi pi-graduation-cap"
                  style={{ fontSize: "1.2em" }}
                />
              }
              className="insight-source-button"
              style={{ width: "2.1em", height: "1.15em" }}
              onClick={getInsightsFromSource}
              type="button"
              tooltip="Get Insights"
              aria-label="Get Insights"
            />
          </div>
        </div> */}

        <div className="ai-button-container">
          <AIButton aiMode={aiMode} handleSetAIButton={handleSetAIButton} />
        </div>

        <TabView className="notes-tabview">
          <TabPanel
            header={
              <span style={{ display: "flex", alignItems: "center" }}>
                <i
                  className="pi pi-align-left"
                  style={{ marginRight: "0.5em" }}
                />
                Notes
              </span>
            }
          >
            <>
              <style>
                {`
                .notes-editor h1,
                .notes-editor h2,
                .notes-editor h3,
                .notes-editor h4 {
                margin-top: 0em !important;
                margin-bottom: 0.3em !important;
                padding-top: 0em !important;
                padding-bottom: 0em !important;
                }
                .notes-editor p,
                .notes-editor ul,
                .notes-editor ol,
                .notes-editor li,
                .notes-editor input[type="checkbox"] {
                font-size: 1em !important;
                margin-top: 0em !important;
                margin-bottom: 0.3em !important;
                padding-top: 0em !important;
                padding-bottom: 0em !important;
                }
                `}
              </style>

              <Editor
                ref={quillRef}
                className="notes-textarea"
                value={currentNotes}
                onTextChange={(e) => setCurrentNotes(e.htmlValue || "")}
              />

              {/* <MDXEditor
                ref={ref}
                className="notes-editor"
                markdown={currentNotes}
                onChange={(value) => setCurrentNotes(value)}
                plugins={[
                  headingsPlugin({
                    allowedHeadingLevels: [1, 2, 3, 4], // Enable all heading levels
                  }),
                  tablePlugin(),
                  linkDialogPlugin(),
                  directivesPlugin(),
                  diffSourcePlugin({ viewMode: "rich-text" }),
                  frontmatterPlugin(),
                  imagePlugin(),
                  listsPlugin(),
                  thematicBreakPlugin(),
                  toolbarPlugin({
                    toolbarClassName: "my-classname",
                    toolbarContents: () => (
                      <>
                        <UndoRedo />
                        <BoldItalicUnderlineToggles />
                        <BlockTypeSelect />
                        <ListsToggle />
                        <InsertTable />
                        <InsertFrontmatter />
                        <InsertThematicBreak />
                        <CreateLink />
                      </>
                    ),
                  }),
                ]}
                contentEditableClassName="notes-textarea"
              /> */}
            </>
          </TabPanel>
          <TabPanel
            header={
              <span style={{ display: "flex", alignItems: "center" }}>
                <i className="pi pi-link" style={{ marginRight: "0.5em" }} />
                Links
              </span>
            }
          >
            <div className="resources-container">
              <div className="matrix-container">
                {currentLinks.map((link, index) => (
                  <div key={index} className="matrix-item">
                    <div className="matrix-item-content">
                      {link.url.includes("youtube.com") ||
                      link.url.includes("youtu.be") ? (
                        <iframe
                          width="200"
                          height="170"
                          src={`https://www.youtube.com/embed/${
                            link.url.includes("v=")
                              ? link.url.split("v=")[1].split("&")[0]
                              : link.url.split("/").pop()?.split("?")[0]
                          }`}
                          title="YouTube video player"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      ) : link.url.endsWith(".pdf") ? (
                        <PdfViewer url={link.url} />
                      ) : (
                        <LinkPreview url={link.url} />
                      )}
                    </div>
                    <div className="matrix-item-title">{link.title}</div>
                    <button
                      className="delete-link-button"
                      onClick={() => handleDeleteLink(index)}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <button className="add-link-button" onClick={handleAddLink}>
              Add Link
            </button>
          </TabPanel>
        </TabView>
        <div className="notes-modal-buttons">
          <button className="notes-button" onClick={handleSaveNotesAndLinks}>
            Save
          </button>
          <button className="notes-button" onClick={handleCancel}>
            Cancel
          </button>
          {/* {source.toLowerCase() === "cvr" && (
            <button
              className="map-button"
              onClick={() => handleMapData(currentNotes)}
            >
              Map the Data (beta)
            </button>
          )} */}
        </div>
      </div>
      {alertMessage && (
        <CustomAlert
          title={alertTitle}
          message={alertMessage}
          htmlMessage={null}
          onClose={() => setAlertMessage(null)}
        />
      )}
      {loading && (
        <div className="progress-overlay">
          <div className="progress-indicator"></div>
          <div className="working">
            <div>Insights</div>
            <div>coming up...</div>
          </div>
        </div>
      )}
      {showAddLinkModal && (
        <div className="add-link-modal-overlay">
          <div className="add-link-modal-content">
            <h3 className="link-label">Subject:</h3>
            <input
              className="subject-input"
              type="text"
              placeholder={`What is it about...`}
              value={currentLink.title}
              onChange={(e) =>
                setCurrentLink((prevValue) => ({
                  title: e.target.value,
                  url: prevValue.url,
                }))
              }
            />
            <h3 className="link-label">Link:</h3>
            <input
              className="add-link-input"
              type="url"
              placeholder={`Add Url reference...`}
              value={currentLink.url}
              onChange={(e) =>
                setCurrentLink((prevValue) => ({
                  title: prevValue.title,
                  url: e.target.value,
                }))
              }
              pattern="https?://.+"
              title="Please enter a valid URL starting with http:// or https://"
            />
            <div className="add-link-modal-buttons">
              <button onClick={handleSaveLink}>Save</button>
              <button onClick={handleCloseLinkModal}>Close</button>
            </div>
          </div>
        </div>
      )}
      <KanbanCard
        visible={showKanbanCard}
        nodeTask={node?.data.task}
        setShowKanbanCard={setShowKanbanCard}
        handleAddUpdateTask={addUpdateTask}
        handleDeleteTask={deleteTask}
      />

      <EventCard
        visible={showEventCard}
        nodeEvent={node?.data.event}
        setShowEventCard={setShowEventCard}
        handleAddUpdateEvent={addUpdateEvent}
        handleDeleteEvent={deleteEvent}
      />

      <AddressCard
        visible={showLocationCard}
        nodeAddress={node?.data.address}
        setShowAddressCard={setShowLocationCard}
        handleAddUpdateAddress={addUpdateAddress}
        handleDeleteAddress={deleteAddress}
      />
    </Dialog>
  );
};

export default NotesModal;
